import React, { useRef, useState } from 'react';
import { X, Upload, Loader2, FileDown, CheckCircle2, AlertTriangle, PlusCircle, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { catalogAPI } from '../services/api';

const LABELS = { name: 'Nom', brand: 'Marque', ref: 'Référence', refs: 'Autres réfs', oem_refs: 'Réfs OEM', ref_brands: 'Marques des réfs', price: 'Prix TTC', original_price: 'Prix barré', category: 'Catégorie', subcategory: 'Sous-catégorie', extra_categories: 'Catégories suppl.', type: 'Type', stock: 'Stock', image: 'Image', images: 'Images', sale_multiple: 'Vendu par', sale_multiple_label: 'Libellé lot', compatibility: 'Compatibilité', fitments: 'Véhicules compatibles' };
const show = (v) => (Array.isArray(v) ? (v.length ? v.join(' | ') : '—') : v == null || v === '' ? '—' : String(v));

// Admin: bulk create / update products from CSV or XLSX
const CatalogImportModal = ({ onClose, onApplied }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('updates');

  const analyse = async (f) => {
    setFile(f); setPreview(null); setResult(null); setError('');
    if (!f) return;
    setBusy(true);
    try {
      const p = await catalogAPI.preview(f);
      setPreview(p);
      setTab(p.updates.some(u => u.changes && Object.keys(u.changes).length) ? 'updates' : p.creates.length ? 'creates' : 'errors');
    } catch (e) {
      setError(e.response?.data?.detail || 'Fichier illisible');
    } finally {
      setBusy(false);
    }
  };

  const changed = preview ? preview.updates.filter(u => u.changes && Object.keys(u.changes).length) : [];
  const unchanged = preview ? preview.updates.length - changed.length : 0;

  const apply = async () => {
    setBusy(true);
    try {
      const r = await catalogAPI.apply(preview.creates, changed);
      setResult(r);
      toast.success(r.message);
      onApplied && onApplied(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import impossible');
    } finally {
      setBusy(false);
    }
  };

  const Tab = ({ id, label, count, color }) => (
    <button type="button" data-testid={`catalog-tab-${id}`} onClick={() => setTab(id)} className={`px-3 py-1.5 rounded-md text-sm font-medium border ${tab === id ? `${color} text-white border-transparent` : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>{label} <span className="ml-1 opacity-80">({count})</span></button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="catalog-import-modal">
        <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><Upload size={20} /> Importer un catalogue produits</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="catalog-close"><X size={22} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {!result && (
            <div className="flex flex-wrap items-center gap-3 bg-gray-50 border rounded-lg p-4">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" data-testid="catalog-file-input" onChange={(e) => analyse(e.target.files?.[0] || null)} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="catalog-choose-file"><Upload size={16} className="mr-2" /> {file ? file.name : 'Choisir un fichier CSV ou Excel'}</Button>
              <button type="button" data-testid="catalog-template" onClick={() => catalogAPI.template()} className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1"><FileDown size={14} /> Modèle CSV</button>
              <button type="button" data-testid="catalog-export" onClick={() => catalogAPI.export()} className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1"><Download size={14} /> Exporter le catalogue actuel</button>
              <p className="text-xs text-gray-500 w-full">La <strong>Référence</strong> sert de clé : référence connue → mise à jour des seules colonnes présentes ; référence inconnue → création (Nom, Marque, Prix TTC, Catégorie et Image obligatoires). Listes séparées par « | ». Catégorie = slug ou nom existant. <strong>Catégories supplémentaires</strong> : « Catégorie &gt; Sous-catégorie | Autre catégorie » (le produit apparaîtra aussi dans ces rayons).</p>
            </div>
          )}
          {busy && !preview && !result && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div>}
          {error && <p className="text-sm text-red-600 flex items-center gap-2" data-testid="catalog-error"><AlertTriangle size={16} /> {error}</p>}

          {preview && !result && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="catalog-mapping">
                {Object.entries(preview.mapping).map(([k, v]) => <span key={k} className="px-2 py-1 rounded-full bg-blue-50 text-blue-800 border border-blue-200">{LABELS[k] || k} ← « {v} »</span>)}
              </div>
              <div className="flex flex-wrap gap-2">
                <Tab id="creates" label="À créer" count={preview.creates.length} color="bg-green-600" />
                <Tab id="updates" label="À mettre à jour" count={changed.length} color="bg-blue-600" />
                <Tab id="errors" label="En erreur" count={preview.errors.length} color="bg-red-600" />
                {unchanged > 0 && <span className="text-xs text-gray-500 self-center" data-testid="catalog-unchanged">{unchanged} ligne(s) identique(s) ignorée(s)</span>}
              </div>

              {tab === 'creates' && (
                preview.creates.length === 0 ? <p className="text-sm text-gray-500">Aucun nouveau produit.</p> : (
                  <div className="border rounded-lg overflow-x-auto"><table className="w-full text-sm min-w-[640px]" data-testid="catalog-creates-table">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase"><tr><th className="px-3 py-2 text-left">Produit</th><th className="px-3 py-2 text-left">Réf.</th><th className="px-3 py-2 text-left">Catégorie</th><th className="px-3 py-2 text-right">Prix TTC</th><th className="px-3 py-2 text-right">Ligne</th></tr></thead>
                    <tbody className="divide-y">{preview.creates.map(c => (
                      <tr key={c.ref} data-testid={`catalog-create-${c.ref}`}><td className="px-3 py-2"><div className="flex items-center gap-2"><img src={c.image} alt="" className="w-8 h-8 object-contain bg-gray-50 rounded" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /><div><p className="font-medium text-gray-900 flex items-center gap-1"><PlusCircle size={12} className="text-green-600" /> {c.name}</p><p className="text-xs text-gray-400">{c.brand}{c.fitments_stats ? <span className="ml-2 text-[#1e3a5f]" data-testid={`catalog-create-fitments-${c.ref}`}>· {c.fitments_stats.engines_matched}/{c.fitments_stats.engines} motorisation(s) reconnue(s)</span> : null}</p></div></div></td><td className="px-3 py-2 font-mono text-xs">{c.ref}</td><td className="px-3 py-2">{c.category}</td><td className="px-3 py-2 text-right">{Number(c.price).toFixed(2)} €</td><td className="px-3 py-2 text-right text-gray-400">{c.line}</td></tr>
                    ))}</tbody></table></div>
                )
              )}
              {tab === 'updates' && (
                changed.length === 0 ? <p className="text-sm text-gray-500">Aucune mise à jour détectée.</p> : (
                  <div className="space-y-2" data-testid="catalog-updates-list">{changed.map(u => (
                    <div key={u.product_id} className="border rounded-lg p-3" data-testid={`catalog-update-${u.product_id}`}>
                      <div className="flex items-center gap-2 mb-2"><img src={u.image} alt="" className="w-8 h-8 object-contain bg-gray-50 rounded" /><div><p className="font-medium text-gray-900 flex items-center gap-1"><RefreshCw size={12} className="text-blue-600" /> {u.name}</p><p className="text-xs text-gray-400">{u.brand} · Réf. {u.ref} · ligne {u.line}</p></div></div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">{Object.entries(u.changes).map(([k, c]) => (
                        <li key={k} className="flex gap-2"><span className="w-28 text-gray-500 flex-shrink-0">{LABELS[k] || k}</span><span className="text-gray-400 line-through truncate max-w-[40%]">{show(c.old)}</span><span className="text-gray-900 font-medium truncate">{show(c.new)}</span></li>
                      ))}</ul>
                    </div>
                  ))}</div>
                )
              )}
              {tab === 'errors' && (
                preview.errors.length === 0 ? <p className="text-sm text-gray-500">Aucune erreur.</p> : (
                  <ul className="text-sm space-y-1" data-testid="catalog-errors-list">{preview.errors.map(e => (
                    <li key={e.line} className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-100 rounded px-3 py-1.5" data-testid={`catalog-error-${e.line}`}><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /><span>Ligne {e.line}{e.ref ? ` (${e.ref})` : ''} : {e.reason}</span></li>
                  ))}</ul>
                )
              )}
            </>
          )}

          {result && (
            <div className="space-y-2" data-testid="catalog-result">
              <p className="text-green-700 font-medium flex items-center gap-2"><CheckCircle2 size={18} /> {result.message}</p>
              {result.errors.length > 0 && <ul className="text-sm text-red-700">{result.errors.map((e, i) => <li key={i}>{e.ref} : {e.reason}</li>)}</ul>}
              <p className="text-xs text-gray-500">Les changements de prix et de stock ont été ajoutés à l'historique (source « import catalogue »).</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="catalog-cancel">{result ? 'Fermer' : 'Annuler'}</Button>
          {preview && !result && <Button data-testid="catalog-apply" onClick={apply} disabled={busy || (!preview.creates.length && !changed.length)} className="bg-green-600 hover:bg-green-700">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />} Appliquer : {preview.creates.length} création(s), {changed.length} mise(s) à jour</Button>}
        </div>
      </div>
    </div>
  );
};

export default CatalogImportModal;
